/**
 * Admin-side Extensions panel — a Sidebar panel that lists installed and
 * available plugins from /plugins/registry.json and lets the user install
 * or uninstall them at runtime via @ais/plugin-host.
 *
 * Implemented as a tiny ViewerPlugin so it uses the exact same Sidebar
 * panel surface (`addPanel/removePanel/refreshPanel`) as third-party
 * plugins do — keeps the UI uniform and proves the contract from the
 * inside. The plugin host is passed in instead of constructed here so the
 * dashboard can reuse the same PluginHost without the browser UI.
 */
import type { Viewer, ViewerPlugin } from '@ais/viewer-core';
import type { Sidebar } from '@ais/viewer-ui';
import type {
  PluginHost,
  PluginRegistryEntry,
  InstalledPluginRecord,
} from '@ais/plugin-host';

/** Construction knobs for the Extensions panel. */
export interface PluginBrowserOptions {
  /**
   * Show the registry-URL controls (input + marketplace link) in the panel,
   * so the operator can point extension fetching at another store — e.g. the
   * trusted-zone marketplace. Default false = today's fixed-registry panel.
   */
  registryControls?: boolean;
  /**
   * This build is served by a host that mirrors its own add-ons (a data hub).
   * Such a host must keep resolving plugins locally, so a SAVED cross-origin
   * registry choice is discarded instead of being re-applied on reload.
   * Default false = the standalone behaviour, where a saved choice is the
   * operator's and nothing overrides it.
   */
  siteBuild?: boolean;
}

export class PluginBrowser implements ViewerPlugin {
  readonly id = 'ais.plugin-browser.v1';
  readonly label = 'Extensions';
  private viewer: Viewer | null = null;
  private sidebar: Sidebar | null = null;
  private host: PluginHost;
  private registryControls = false;
  private registryOpen = false;   // App-store row disclosure (closed by default)
  private registryStorageKey = '';
  private siteBuild = false;
  private registry: PluginRegistryEntry[] = [];
  private status: string = 'Loading registry…';
  private offHostChange: (() => void) | null = null;

  constructor(host: PluginHost, opts: PluginBrowserOptions = {}) {
    this.host = host;
    this.registryControls = !!opts.registryControls;
    this.siteBuild = opts.siteBuild ?? false;
    this.registryStorageKey = `@ais/plugin-host:registry-url:${location.pathname}`;
    // Apply a previously chosen registry BEFORE the first refresh, so reload
    // keeps pointing at the operator's store (e.g. the trusted-zone one).
    //
    // EXCEPT a cross-origin one on a site build: that host mirrors its add-ons and
    // serves its own registry so it survives losing the store, and a saved remote
    // choice quietly undoes that on every reload — including one written months ago
    // by a checkpoint that carried a registry marker. The stale entry is dropped
    // rather than merely ignored, or it keeps winning on the next build that trusts
    // it. Same-origin saved values still apply: those ARE this site.
    if (this.registryControls) {
      try {
        const saved = localStorage.getItem(this.registryStorageKey);
        if (saved) {
          const remote = new URL(saved, window.location.href).origin !== window.location.origin;
          if (remote && this.siteBuild) {
            localStorage.removeItem(this.registryStorageKey);
            console.info(`[plugin] dropped a saved remote registry (${saved}) — this site serves its own`);
          } else {
            this.host.setRegistryUrl(saved);
          }
        }
      } catch {
        /* storage unavailable — stay on the default */
      }
    }
  }

  /** Persist + apply a registry choice ('' = back to the default). */
  applyRegistryUrl(url: string): void {
    const v = (url || '').trim();
    try {
      if (v) localStorage.setItem(this.registryStorageKey, v);
      else localStorage.removeItem(this.registryStorageKey);
    } catch {
      /* best-effort persistence */
    }
    this.host.setRegistryUrl(v);
    void this.refreshRegistry();
  }

  /** The marketplace UI that pairs with a registry endpoint (…/plugins/registry.json -> …/marketplace/). */
  private marketplaceUrl(): string {
    const reg = this.host.getRegistryUrl();
    const abs = new URL(reg, window.location.href);
    return new URL(abs.pathname.replace(/\/plugins\/registry\.json$/i, '/marketplace/'), abs).toString();
  }

  install(viewer: Viewer): void {
    this.viewer = viewer;
    const sidebar = viewer.ui.sidebar as Sidebar | undefined;
    if (!sidebar?.addPanel) {
      console.warn(`[${this.id}] no Sidebar.addPanel — extensions browser disabled`);
      return;
    }
    this.sidebar = sidebar;
    // Repaint the panel whenever the host's installed-set changes — including
    // the async tail of restoreInstalled(). Without this, a page reload paints
    // the panel before restoration finishes and the row reads "Install" until
    // some unrelated event triggers a refresh.
    this.offHostChange = this.host.onChange(() => {
      this.sidebar?.refreshPanel(this.id);
    });
    sidebar.addPanel({
      id: this.id,
      label: this.label,
      icon: 'cube',
      actions: [
        {
          icon: 'refresh',
          tip: 'Refresh registry',
          onClick: () => void this.refreshRegistry(),
        },
      ],
      render: (body) => this.renderPanel(body),
    });
    void this.refreshRegistry();
  }

  uninstall(_viewer: Viewer): void {
    this.offHostChange?.();
    this.offHostChange = null;
    this.sidebar?.removePanel(this.id);
    this.sidebar = null;
    this.viewer = null;
  }

  private async refreshRegistry(): Promise<void> {
    try {
      const all = await this.host.listAvailable(true);
      // Dashboard widgets live in /plugins/registry.json alongside viewer
      // plugins (the marketplace serves both kinds). They don't implement
      // ViewerPlugin and would fail at install — hide them from the admin's
      // Extensions panel. Missing `kind` defaults to a viewer plugin.
      this.registry = all.filter((e) => (e.kind ?? 'viewer-plugin') === 'viewer-plugin');
      this.status = this.registry.length === 0 ? 'No plugins published yet.' : '';
    } catch (e) {
      this.status = `Registry error: ${(e as Error).message}`;
    }
    this.sidebar?.refreshPanel(this.id);
  }

  /**
   * Registry controls: where this panel fetches extensions from. The input
   * shows the CURRENT endpoint (the origin-local default until changed);
   * Apply persists it (per-mount localStorage) and refetches; the ↗ button
   * opens the paired marketplace UI in a new tab.
   */
  private renderRegistryControls(body: HTMLElement): void {
    // Disclosure row — always visible (compact); the URL input + open button
    // only render while the row is OPEN. Default closed: most sessions never
    // touch the store source, so the panel stays clean.
    const row = document.createElement('div');
    row.className = 'pb-registry-toggle';
    row.style.cssText =
      'display:flex;align-items:center;gap:6px;margin-bottom:6px;cursor:pointer;user-select:none;opacity:.85';
    const chev = document.createElement('span');
    chev.textContent = this.registryOpen ? '\u25be' : '\u25b8';
    const lbl = document.createElement('span');
    lbl.textContent = 'App store';
    lbl.style.cssText = 'font-size:11px;letter-spacing:.3px';
    row.appendChild(chev);
    row.appendChild(lbl);
    row.title = 'Where extensions are fetched from (click to configure)';
    row.addEventListener('click', () => {
      this.registryOpen = !this.registryOpen;
      this.sidebar?.refreshPanel(this.id);
    });
    body.appendChild(row);

    if (!this.registryOpen) return;

    const wrap = document.createElement('div');
    wrap.className = 'pb-registry';
    wrap.style.cssText = 'display:flex;gap:4px;margin-bottom:8px;align-items:center';

    const input = document.createElement('input');
    input.type = 'text';
    input.value = this.host.getRegistryUrl();
    input.placeholder = '/plugins/registry.json';
    input.title =
      'App-store registry URL (default: this site\u2019s /plugins/registry.json; e.g. the trusted-zone store: https://dmz.bzbsl.com/plugins/registry.json)';
    input.style.cssText = 'flex:1;min-width:0;font-size:11px;padding:3px 6px';

    const apply = document.createElement('button');
    apply.type = 'button';
    apply.textContent = 'Apply';
    apply.title = 'Use this store (persisted for this app)';
    apply.addEventListener('click', () => this.applyRegistryUrl(input.value));
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this.applyRegistryUrl(input.value);
    });

    const open = document.createElement('button');
    open.type = 'button';
    open.textContent = '\u2197';
    open.title = 'Open the App store in a new tab';
    open.addEventListener('click', () => window.open(this.marketplaceUrl(), '_blank', 'noopener'));

    wrap.appendChild(input);
    wrap.appendChild(apply);
    wrap.appendChild(open);
    body.appendChild(wrap);
  }

  private renderPanel(body: HTMLElement): void {
    if (this.registryControls) this.renderRegistryControls(body);
    const installed = new Map(
      this.host.listInstalled().map((r) => [r.id, r] as [string, InstalledPluginRecord]),
    );

    if (this.status) {
      const s = document.createElement('div');
      s.className = 'pb-status';
      s.textContent = this.status;
      body.appendChild(s);
    }

    if (this.registry.length === 0 && installed.size === 0) return;

    const list = document.createElement('ul');
    list.className = 'pb-list';
    body.appendChild(list);

    // Show every registry entry plus any installed plugin not in the
    // registry (e.g. installed when an entry was present, then removed —
    // user can still uninstall locally).
    const seen = new Set<string>();
    const rows: Array<{
      id: string;
      label: string;
      version: string;
      author?: string;
      description?: string;
      installedRecord: InstalledPluginRecord | null;
      registryEntry: PluginRegistryEntry | null;
    }> = [];
    for (const e of this.registry) {
      seen.add(e.id);
      rows.push({
        id: e.id,
        label: e.label,
        version: e.version,
        author: e.author,
        description: e.description,
        installedRecord: installed.get(e.id) ?? null,
        registryEntry: e,
      });
    }
    for (const r of installed.values()) {
      if (seen.has(r.id)) continue;
      rows.push({
        id: r.id,
        label: r.id,
        version: r.version,
        installedRecord: r,
        registryEntry: null,
      });
    }

    for (const row of rows) {
      const li = document.createElement('li');
      li.className = 'pb-row';

      const head = document.createElement('div');
      head.className = 'pb-row-head';
      const title = document.createElement('span');
      title.className = 'pb-row-title';
      title.textContent = `${row.label} · ${row.version}`;
      head.appendChild(title);

      const action = document.createElement('button');
      action.type = 'button';
      action.className = 'pb-action';
      if (row.installedRecord) {
        action.textContent = 'Uninstall';
        action.addEventListener('click', () => {
          this.host.uninstall(row.id);
          this.sidebar?.refreshPanel(this.id);
        });
      } else if (row.registryEntry) {
        action.textContent = 'Install';
        action.addEventListener('click', () => void this.doInstall(row.registryEntry!, action));
      } else {
        action.style.display = 'none';
      }
      head.appendChild(action);
      li.appendChild(head);

      if (row.description) {
        const d = document.createElement('div');
        d.className = 'pb-row-desc';
        d.textContent = row.description;
        li.appendChild(d);
        // The description is clamped to 3 lines; long ones get a Show more/less
        // toggle so the Extensions list stays compact (no endless scrolling).
        if (row.description.length > 150) {
          const more = document.createElement('button');
          more.type = 'button';
          more.className = 'pb-row-more';
          more.textContent = 'Show more';
          more.addEventListener('click', () => {
            const expanded = d.classList.toggle('pb-row-desc--expanded');
            more.textContent = expanded ? 'Show less' : 'Show more';
          });
          li.appendChild(more);
        }
      }
      if (row.author) {
        const a = document.createElement('div');
        a.className = 'pb-row-author';
        a.textContent = `by ${row.author}`;
        li.appendChild(a);
      }
      list.appendChild(li);
    }
  }

  private async doInstall(entry: PluginRegistryEntry, btn: HTMLButtonElement): Promise<void> {
    btn.disabled = true;
    btn.textContent = 'Installing…';
    try {
      await this.host.install(entry);
      this.viewer?.setStatus(`Installed plugin '${entry.id}@${entry.version}'`);
    } catch (e) {
      this.viewer?.setStatus(`Install failed: ${(e as Error).message}`);
      btn.disabled = false;
      btn.textContent = 'Install';
      return;
    }
    this.sidebar?.refreshPanel(this.id);
  }
}
